/**
 * Shared sale / compare / badge math for PriceBlock surfaces
 * (storefront preview, checkout UI, post-purchase).
 *
 * - Compare stays the product’s Shopify compare-at when present.
 * - App discount applies to the real (sale) price only.
 * - Badge % is total savings vs compare (or vs pre-app-discount price).
 */

export function toMoneyNumber(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "object" && value.amount != null) {
    return toMoneyNumber(value.amount);
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeDiscountType(type) {
  const t = String(type || "").toLowerCase();
  if (t === "percentage" || t === "percent") return "percentage";
  if (t === "amount" || t === "fixed") return "amount";
  return t;
}

export function defaultCalculateDiscountedPrice(price, discountValue, discountType) {
  const base = toMoneyNumber(price);
  const discount = toMoneyNumber(discountValue);
  if (!(discount > 0)) return base;
  const type = normalizeDiscountType(discountType);
  if (type === "percentage") {
    return Math.max(0, base - (base * discount) / 100);
  }
  if (type === "amount") {
    return Math.max(0, base - discount);
  }
  return base;
}

/**
 * @returns {{ effectiveSale: number, compareBase: number, savedAmount: number, discountPercent: number }}
 */
export function computeOfferPriceAmounts({
  price,
  compareAtPrice,
  sellingPlanDiscount = 0,
  discountValue,
  discountType,
  calculateDiscountedPriceFn = defaultCalculateDiscountedPrice,
} = {}) {
  const basePrice = Math.max(0, toMoneyNumber(price));
  const shopifyCompare = Math.max(0, toMoneyNumber(compareAtPrice));
  const planDiscount = Math.max(0, toMoneyNumber(sellingPlanDiscount));
  const afterPlan = Math.max(0, basePrice - planDiscount);

  const type = normalizeDiscountType(discountType);
  const rawDiscount = toMoneyNumber(discountValue);
  const hasDiscount = rawDiscount > 0;
  // Only apply when discount is actively > 0. A value of 0 must keep full price
  // (some callers' calculators return 0 for missing/0 discount and looked like 100% off).
  const discountPrice = hasDiscount
    ? calculateDiscountedPriceFn(
        afterPlan,
        rawDiscount,
        type === "percentage" ? "percentage" : type
      )
    : afterPlan;
  const effectiveSale =
    hasDiscount &&
    Number.isFinite(discountPrice) &&
    discountPrice < afterPlan
      ? Math.max(0, discountPrice)
      : afterPlan;

  const compareBase =
    shopifyCompare > effectiveSale
      ? shopifyCompare
      : afterPlan > effectiveSale
        ? afterPlan
        : 0;

  const savedAmount = compareBase
    ? Math.max(0, compareBase - effectiveSale)
    : Math.max(0, afterPlan - effectiveSale);

  const discountPercent =
    compareBase > 0 && savedAmount > 0
      ? Math.round((savedAmount / compareBase) * 100)
      : 0;

  return {
    effectiveSale,
    compareBase,
    savedAmount,
    discountPercent,
  };
}

export function resolveVariantCompareAt(variant) {
  if (!variant) return 0;
  return toMoneyNumber(variant.compare_at_price ?? variant.compareAtPrice);
}

export function resolveVariantPrice(variant) {
  if (!variant) return 0;
  return toMoneyNumber(variant.price);
}
